import { ScheduleSlug } from "../../Schedule/Schedule.Interface";
import { LocationVarKey } from "../Generated/LocationVarKey.Data";
import { VarDef } from "../Generated/VarDef.Data";
import { VarKey } from "../Generated/VarKey.Data";
import { DISCOUNTS } from "./CortexDiscounts.Data";
import { FACILITY_AND_SPACE_FEATURES } from "./CortexFacilityFeatures.Data";
import { FACILITY_INFO, FacilityInfoKey } from "./CortexFacilityInfo.Data";
import { URLS } from "./CortexURLs.Data";

/**
 * Single (legacy → cortex) copy spec used by the migration preview/apply flow.
 *
 * Each spec describes one legacy LocationVarKey whose values should be
 * copied into EITHER a Cortex LocationVarKey (the original target) OR a
 * column on a `RoutingGroupEntity` row identified by slug (the 2026-06-05
 * extension for emergency consolidation). Scope is preserved by the
 * caller in both cases — this spec is a pure mapping, not a per-row
 * instruction.
 *
 * See docs/snowy/plans/2026-05-01-cortex-v3-migration-admin.md and
 * docs/snowy/plans/2026-06-05-emergency-consolidation.md.
 */
export type RoutingGroupTargetColumn =
  | "escalationForwardPhoneNumber"
  | "outsideHoursForwardPhoneNumber"
  | "alertSmsRecipients"
  | "alertEmailRecipients";

export interface CortexMigrationSpec {
  /** Stable id for UI selection state, e.g. "facility-feature.climate_control". */
  id: string;
  /** Top-level group label for UI section headers, e.g. "Facility Features". */
  groupLabel: string;
  /** Per-spec human label, e.g. "Climate Control". */
  label: string;
  /**
   * Legacy LocationVar source. Absent for entity-targeted specs
   * (`brandColors`), which read their own entity rows instead — every
   * consumer collecting fromKeys must filter the undefined.
   */
  fromKey?: LocationVarKey;
  /** Extra raw legacy keys read with `fromKey` at the same scope (for settings stored under more than one spelling); values merge with `fromKey` winning, and boolean specs whose sources disagree are quarantined. */
  additionalFromKeys?: string[];
  /** Target LocationVar to write into. Mutually exclusive with `routingGroup`. */
  toKey?: LocationVarKey;
  /**
   * Target RoutingGroupEntity column at the same scope as the legacy
   * LocationVar row. Mutually exclusive with `toKey`. The migration
   * apply/preview code finds-or-creates the named routing-group row at
   * the source scope and writes the column.
   */
  routingGroup?: {
    slug: string;
    column: RoutingGroupTargetColumn;
  };
  /** Target Schedule (weekly hours) at the same scope; the service parses the freeform hours (no `transform`, since the parser can't be imported into `types`) and quarantines unparseable strings. */
  schedule?: {
    slug: ScheduleSlug;
    label: string;
  };
  /**
   * Target the Brand entity's `colors` JSONB (ENG-2893): one preview row per
   * brand; `colors.primary/secondary` derived from
   * `theme.primaryBackground/secondaryBackground` (DefaultTheme value with a
   * warning when a side is missing). Mutually exclusive with the other
   * targets; the service owns the entity read/write.
   */
  brandColors?: true;
  /**
   * Optional value transform. Returns a string for `toKey` specs and for
   * scalar routing-group columns; returns a `string[]` for array-valued
   * routing-group columns (`alertSmsRecipients`, `alertEmailRecipients`)
   * — typically parsing the legacy CSV string. Returns `null` to SKIP the
   * write (e.g. a discount body still at its boilerplate default).
   */
  transform?: (raw: string | null, siblings?: { name?: string | null; text?: string | null }) => string | string[] | null;
  /**
   * Extra legacy source keys read at the same scope and passed to `transform` as `siblings`, so a
   * single target (e.g. a feature-unit blob) can be assembled from a toggle PLUS its display name /
   * custom text. Only used by feature specs today; other specs ignore `siblings`.
   */
  assembleKeys?: { name?: LocationVarKey; text?: LocationVarKey };
  /**
   * Marks a free-text value that may carry legacy `#*Location:X*#` variable tokens. The migration rewrites
   * them to Snowy `{{Location:CortexX}}` tokens (see `convertLegacyTokens`) and quarantines the row when a
   * token has no known replacement, so template syntax the new bot can't resolve never reaches a prompt.
   */
  convertsLegacyTokens?: boolean;
  /** Marks the source as an on/off `Has*` flag; the migration normalizes it and quarantines any value that isn't a recognized boolean. */
  isBooleanFlag?: boolean;
  /** Marks the value as an IANA timezone; the migration validates it and quarantines anything that isn't a real zone. */
  isTimezone?: boolean;
  /**
   * The value identifies ONE facility (its name, address, FMS id), so every location keeps its own
   * row: never collapsed onto the org or a group, and legacy org/group rows are not copied (there
   * they hold the org's or a district's own label, e.g. "Barry's District", not a facility's value).
   * The planner materializes it per location from the effective legacy value (ENG-3388 D-5 / C-13).
   */
  perLocation?: true;
  /** Meaningless above a location: org/group rows resolve but never write. */
  locationScopeOnly?: true;
  /**
   * The target is the gate-code keypad instruction. Every source key's value at a scope is read
   * together (the keypad format can be in either legacy message), and the service turns it into a
   * clean instruction, flags prose it can't read safely (`unclear-keypad`), or writes nothing when
   * there is no keypad information.
   */
  isKeypadInstruction?: true;
}

/** Tri-state classification of a legacy on/off flag value. */
export type LegacyFlagState = "on" | "off" | "unrecognized";

/** Reads a legacy flag value as on/off/unrecognized, trimming and lower-casing first so messy encodings like `"TRUE "` still count as on. */
export function classifyLegacyFlag(raw: string | null): LegacyFlagState {
  if (raw == null) return "off";
  const normalized = raw.trim().toLowerCase();
  if (normalized === "") return "off";
  if (normalized === "1" || normalized === "true") return "on";
  if (normalized === "0" || normalized === "false") return "off";
  return "unrecognized";
}

const GROUP_FACILITY_FEATURES = "Facility Features";
const GROUP_FACILITY_FEATURES_SERIALIZED = "Facility Features — serialized values (June 2026 format)";
const GROUP_LINKS = "Links";
const GROUP_FACILITY_INFO = "Facility Info";
const GROUP_VOICE = "Voice";
const GROUP_ADMIN = "Admin";
const GROUP_EMERGENCY_ROUTING = "Routing Groups — Emergency";
const GROUP_HOURS = "Hours";
const GROUP_GATE_CODE = "Gate Code";
const GROUP_DISCOUNTS = "Discounts";
const GROUP_BRANDS = "Brands";

/** A legacy var's default value, for the "did the operator customize it?" comparison. */
function legacyDefault(key: LocationVarKey): string {
  return (VarDef[("Location:" + key) as VarKey]?.defaultValue ?? "").trim();
}

/** Copy the legacy prose only when the operator changed it from the boilerplate default; else skip (null). */
function migrateIfCustomized(defaultValue: string): (raw: string | null) => string | null {
  return (raw) => {
    const v = (raw ?? "").trim();
    return v && v !== defaultValue ? v : null;
  };
}

/** Parse a legacy CSV recipient string into an array of trimmed entries. Used by the alert-recipient specs. */
function parseCsvRecipients(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;\n]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/** True only when `classifyLegacyFlag` reads the value as on (unrecognized junk is not on — the migration quarantines that separately). */
function isLegacyFlagOn(raw: string | null): boolean {
  return classifyLegacyFlag(raw) === "on";
}

/**
 * The June 2026 per-feature storage: one JSON string per feature switch var holding on/off,
 * custom wording, and the display-name override together. Rejected 2026-09-10 (ENG-3209,
 * decision 20) in favour of one plain variable per property. Any row still holding this shape
 * is converted by the three `*.unblob*` specs below; once every environment is converted the
 * group previews as "nothing to write" and can be removed.
 */
interface LegacySerializedFeature {
  on?: unknown;
  useCustomText?: unknown;
  customText?: unknown;
  useNameOverride?: unknown;
  nameOverride?: unknown;
}

function parseLegacySerializedFeature(raw: string | null): LegacySerializedFeature | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === "object" ? (parsed as LegacySerializedFeature) : null;
  } catch {
    return null;
  }
}

/**
 * Flag state, tolerating the June-2026 serialized feature unit whose `on` property carries the flag.
 *
 * `classifyLegacyFlag` alone reports `{"on":true,…}` as "unrecognized", which silently turned into
 * "no confident value" everywhere a legacy boolean is read through a transform. 1,637 rows across 6
 * orgs still hold that shape, so that is not an edge case. Kept separate from `classifyLegacyFlag`
 * rather than folded into it: that function is read by callers (customization-report row
 * classification, the studio's toggle rendering) where "this value is not a plain flag" is the
 * answer they want.
 */
export function classifyLegacyFlagOrFeature(raw: string | null): LegacyFlagState {
  const blob = parseLegacySerializedFeature(raw);
  if (blob) return blob.on === true ? "on" : blob.on === false ? "off" : "unrecognized";
  return classifyLegacyFlag(raw);
}

/** A non-empty string field of the blob, only when its "use" flag was on; else null (nothing to write). */
function enabledText(enabled: unknown, value: unknown): string | null {
  if (enabled !== true || typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Facility-info fields that belong to exactly one facility. Collapsing them made one location's
 * value the org's or a district's (prod org 146: the org was renamed "Carraway RV & Boat", and every
 * location inheriting it was renamed when CX corrected the org). See `CortexMigrationSpec.perLocation`.
 */
const PER_LOCATION_FACILITY_INFO: ReadonlySet<FacilityInfoKey> = new Set([
  FacilityInfoKey.Name,
  FacilityInfoKey.Address,
  FacilityInfoKey.PostalCode,
  FacilityInfoKey.LocationNumber,
  FacilityInfoKey.FMSId
]);

/** Maps each feature flag's corrupted-but-widely-deployed spelling (the registry's `legacyHasKey`) to its correct spelling, since some orgs hold data only under the correct one and the migration must read both. */
const LEGACY_FLAG_ALIASES: Partial<Record<LocationVarKey, string[]>> = {
  [LocationVarKey.HasVideoSurveillanceveillance]: ["HasVideoSurveillance"],
  [LocationVarKey.HasHeatedUnitsts]: ["HasHeatedUnits"],
  [LocationVarKey.HasConferenceRoomnceRoom]: ["HasConferenceRoom"],
  // ENG-3209: a plain parking space IS an outdoor space, so the legacy outdoor-vehicle flag seeds the one `parking` switch.
  [LocationVarKey.HasVehicleStorage]: ["HasOutdoorVehicleStorage"]
};

/**
 * Build the canonical list of (legacy → Cortex) copy specs by walking every
 * Cortex registry. Same surface as the deprecated CLI script's `buildCopySpecs()`.
 *
 * Order is stable and grouped by `groupLabel` so the studio UI can render
 * sections without re-sorting.
 */
/**
 * Legacy → Cortex copies the location URL service depends on (ENG-3138). Each new Cortex variable
 * is a straight copy of the legacy one operators have been filling in for years; without this
 * backfill a Snowy link resolves to nothing on every existing location.
 */
const LOCATION_URL_BACKFILL: ReadonlyArray<[LocationVarKey, LocationVarKey, string]> = [
  [LocationVarKey.WebsiteProvider, LocationVarKey.CortexWebsiteProvider, "Website Provider"],
  [LocationVarKey.SpotBrandSlug, LocationVarKey.CortexSpotBrandSlug, "spot Brand Slug"],
  // The LOCATION slug, not the short-link slug — spot URLs resolve `/{brand}/{location}` against it.
  [LocationVarKey.SpotLocationSlug, LocationVarKey.CortexSpotLocationSlug, "spot Location Slug"],
  // The short-link slug behind the QR code (my.spot.is/{brand-short}/{this}/qr) — the v3 spot.is page edits it.
  [LocationVarKey.SpotLinkSlug, LocationVarKey.CortexSpotLinkSlug, "spot Short Link Slug"],
  [LocationVarKey.SpotReferralURL, LocationVarKey.CortexSpotReferralURL, "spot Referral URL"],
  [LocationVarKey.StoragelyClientID, LocationVarKey.CortexStoragelyClientID, "Storagely Client ID"],
  [LocationVarKey.StoragePugSingleLocationBaseURL, LocationVarKey.CortexStoragePugSingleLocationBaseURL, "StoragePug Single-Location Base URL"],
  [LocationVarKey.URLLookupPattern, LocationVarKey.CortexURLLookupPattern, "Website URL Lookup Pattern"]
];

export function buildCortexMigrationSpecs(): CortexMigrationSpec[] {
  const specs: CortexMigrationSpec[] = [];

  for (const f of FACILITY_AND_SPACE_FEATURES) {
    // Unit-only tags have no legacy Has* flag and no switch — FMS sync is their only source.
    if (!f.legacyHasKey || !f.cortexVarKey) continue;
    // Three plain vars per feature (ENG-3209): the legacy Has* flag seeds the "0"/"1" switch; the
    // operator's legacy display name / custom text copy into their own Cortex text vars, but only
    // when customized off the legacy default (else the Cortex var stays empty = catalog default).
    specs.push({
      id: `facility-feature.${f.key}`,
      groupLabel: GROUP_FACILITY_FEATURES,
      label: f.label,
      fromKey: f.legacyHasKey,
      additionalFromKeys: LEGACY_FLAG_ALIASES[f.legacyHasKey],
      toKey: f.cortexVarKey,
      isBooleanFlag: true,
      transform: (raw) => (isLegacyFlagOn(raw) ? "1" : "0")
    });
    if (f.legacyNameKey) {
      specs.push({
        id: `facility-feature.${f.key}.name`,
        groupLabel: GROUP_FACILITY_FEATURES,
        label: `${f.label} display name`,
        fromKey: f.legacyNameKey,
        toKey: f.nameVarKey,
        convertsLegacyTokens: true,
        transform: migrateIfCustomized(legacyDefault(f.legacyNameKey))
      });
    }
    if (f.legacyTextKey && f.botWordingVarKey) {
      specs.push({
        id: `facility-feature.${f.key}.text`,
        groupLabel: GROUP_FACILITY_FEATURES,
        label: `${f.label} wording`,
        fromKey: f.legacyTextKey,
        toKey: f.botWordingVarKey,
        convertsLegacyTokens: true,
        transform: migrateIfCustomized(legacyDefault(f.legacyTextKey))
      });
    }
  }

  // Serialized-value conversion (decision 20): a switch var still holding the June blob is
  // read once and written back as three plain vars. A row already holding "0"/"1" transforms to
  // null on every spec, so a converted environment previews as nothing to write. Deliberately
  // NOT `isBooleanFlag` — the migrator would quarantine the JSON as an unrecognized flag.
  for (const f of FACILITY_AND_SPACE_FEATURES) {
    if (!f.cortexVarKey || !f.botWordingVarKey) continue;
    const switchKey = f.cortexVarKey;
    specs.push({
      id: `facility-feature.${f.key}.unblob`,
      groupLabel: GROUP_FACILITY_FEATURES_SERIALIZED,
      label: `${f.label} on/off`,
      fromKey: switchKey,
      toKey: switchKey,
      transform: (raw) => {
        const blob = parseLegacySerializedFeature(raw);
        return blob ? (blob.on === true ? "1" : "0") : null;
      }
    });
    specs.push({
      id: `facility-feature.${f.key}.unblob.name`,
      groupLabel: GROUP_FACILITY_FEATURES_SERIALIZED,
      label: `${f.label} display name`,
      fromKey: switchKey,
      toKey: f.nameVarKey,
      convertsLegacyTokens: true,
      transform: (raw) => {
        const blob = parseLegacySerializedFeature(raw);
        return blob ? enabledText(blob.useNameOverride, blob.nameOverride) : null;
      }
    });
    specs.push({
      id: `facility-feature.${f.key}.unblob.wording`,
      groupLabel: GROUP_FACILITY_FEATURES_SERIALIZED,
      label: `${f.label} wording`,
      fromKey: switchKey,
      toKey: f.botWordingVarKey,
      convertsLegacyTokens: true,
      transform: (raw) => {
        const blob = parseLegacySerializedFeature(raw);
        return blob ? enabledText(blob.useCustomText, blob.customText) : null;
      }
    });
  }

  // Discounts: the v3 model has no toggle — an empty details var means "not offered". Migrate the
  // operator's per-discount prose (and the custom display name) into the Cortex details vars, but
  // ONLY when they customized it off the legacy boilerplate default (else leave the v3 field empty).
  for (const d of DISCOUNTS) {
    // Gate on the legacy `HasDiscount*` flag: OFF → not offered → migrate nothing. ON → pull the
    // operator's text/name, but only when they customized it off the boilerplate default.
    if (d.legacyHasKey && d.legacyDetailsKey) {
      const detailsFn = migrateIfCustomized(legacyDefault(d.legacyDetailsKey));
      specs.push({
        id: `discount.${d.key}.details`,
        groupLabel: GROUP_DISCOUNTS,
        label: `${d.label} details`,
        fromKey: d.legacyHasKey,
        assembleKeys: { text: d.legacyDetailsKey },
        toKey: d.detailsVarKey,
        convertsLegacyTokens: true,
        transform: (raw, siblings) => (isLegacyFlagOn(raw) ? detailsFn(siblings?.text ?? null) : null)
      });
    }
    if (d.legacyHasKey && d.legacyNameKey && d.nameVarKey) {
      const nameFn = migrateIfCustomized(legacyDefault(d.legacyNameKey));
      specs.push({
        id: `discount.${d.key}.name`,
        groupLabel: GROUP_DISCOUNTS,
        label: `${d.label} display name`,
        fromKey: d.legacyHasKey,
        assembleKeys: { name: d.legacyNameKey },
        toKey: d.nameVarKey,
        convertsLegacyTokens: true,
        transform: (raw, siblings) => (isLegacyFlagOn(raw) ? nameFn(siblings?.name ?? null) : null)
      });
    }
  }

  for (const l of URLS) {
    if (l.legacyURLKey && l.legacyHasURLKey) {
      // Gated link (same shape as Pay-By-Phone below): the legacy on/off flag decides whether the URL
      // migrates. The URLs page has no visible control for the Cortex `…Enabled` flag (typing a URL
      // sets it), so a URL copied under a legacy "off" would show as set while the bot never shares it.
      specs.push({
        id: `link.${l.key}.url`,
        groupLabel: GROUP_LINKS,
        label: `${l.label} URL`,
        fromKey: l.legacyHasURLKey,
        assembleKeys: { text: l.legacyURLKey },
        toKey: l.urlVarKey,
        transform: (raw, siblings) => {
          if (!isLegacyFlagOn(raw)) return null;
          const url = (siblings?.text ?? "").trim();
          return url || null;
        }
      });
    } else if (l.legacyURLKey) {
      specs.push({
        id: `link.${l.key}.url`,
        groupLabel: GROUP_LINKS,
        label: `${l.label} URL`,
        fromKey: l.legacyURLKey,
        toKey: l.urlVarKey
      });
    }
    if (l.legacyHasURLKey) {
      specs.push({
        id: `link.${l.key}.enabled`,
        groupLabel: GROUP_LINKS,
        label: `${l.label} enabled`,
        fromKey: l.legacyHasURLKey,
        toKey: l.enabledVarKey
      });
    }
  }

  // Location URL service (ENG-3138). Snowy resolves links only from `Cortex*` variables, so the
  // values behind them — the spot slugs, the website provider, the provider-specific ids — need a
  // Cortex home. Straight copies; the legacy keys stay put for legacy code.
  for (const [fromKey, toKey, label] of LOCATION_URL_BACKFILL) {
    specs.push({ id: `location-url.${toKey}`, groupLabel: GROUP_LINKS, label, fromKey, toKey });
  }

  for (const fi of FACILITY_INFO) {
    if (!fi.legacyKey) continue; // v3-only fields (e.g. Persona) have no legacy backfill.
    if (fi.legacyHasKey) {
      // Gated field (e.g. Pay-By-Phone): the legacy on/off flag decides whether the value migrates.
      specs.push({
        id: `facility-info.${fi.key}`,
        groupLabel: GROUP_FACILITY_INFO,
        label: fi.label,
        fromKey: fi.legacyHasKey,
        assembleKeys: { text: fi.legacyKey },
        toKey: fi.cortexVarKey,
        transform: (raw, siblings) => {
          if (!isLegacyFlagOn(raw)) return null;
          const text = (siblings?.text ?? "").trim();
          return text || null;
        }
      });
      continue;
    }
    specs.push({
      id: `facility-info.${fi.key}`,
      groupLabel: GROUP_FACILITY_INFO,
      label: fi.label,
      fromKey: fi.legacyKey,
      toKey: fi.cortexVarKey,
      ...(PER_LOCATION_FACILITY_INFO.has(fi.key) ? { perLocation: true as const } : {}),
      // An FMS id names one facility and nothing renders it, so it never sits above a location.
      ...(fi.key === FacilityInfoKey.FMSId ? { locationScopeOnly: true as const } : {})
    });
  }

  // FMS sync enablement (ENG-3388 D-4): explicit Cortex toggle backfilled from the legacy flag.
  specs.push({
    id: "facility-info.fms_sync_enabled",
    groupLabel: GROUP_FACILITY_INFO,
    label: "FMS Sync Enabled",
    fromKey: LocationVarKey.FMSSyncEnabled,
    toKey: LocationVarKey.CortexFacilityFMSSyncEnabled,
    isBooleanFlag: true,
    transform: (raw) => (isLegacyFlagOn(raw) ? "1" : "0")
  });

  // Pricing fee scalars retired 2026-07-17 — fee amounts live in the policy
  // worksheets (opt-in amount fields), so there is no Cortex var to migrate to.

  // Gate-code keypad entry ("press *, enter your code, then #"), which the bot appends when it gives a
  // code. Operators wrote the format into either the FMS gate-code SMS template or the third gate-code
  // message, so both are read.
  specs.push({
    id: "gate-code.keypad-instructions",
    groupLabel: GROUP_GATE_CODE,
    label: "Gate code keypad instructions",
    fromKey: LocationVarKey.FMSGateCodeChannelSMSMessage,
    additionalFromKeys: [LocationVarKey.AccessGateCode3],
    toKey: LocationVarKey.CortexFacilityGateCodeInstructions,
    isKeypadInstruction: true
  });

  // Voice pre-welcome message — read by BotConference before the bot's
  // standard greeting. Lives as a setting on the greeting Cortex topic.
  specs.push({
    id: "voice.pre-welcome-message",
    groupLabel: GROUP_VOICE,
    label: "Pre-welcome message",
    fromKey: LocationVarKey.VoicePreWelcomeMessage,
    toKey: LocationVarKey.CortexVoicePreWelcomeMessage,
    convertsLegacyTokens: true
  });

  // SMS campaigns capability flag — gates the Contact Center v3 "New SMS"
  // button (ENG-2946). Admin-managed, org scope (Details v3 → Admin page);
  // the legacy HasSMSCampaigns stays behind for the v2 surface.
  specs.push({
    id: "sms-campaigns.enabled",
    groupLabel: GROUP_ADMIN,
    label: "SMS campaigns enabled",
    fromKey: LocationVarKey.HasSMSCampaigns,
    toKey: LocationVarKey.CortexIsSMSCampaignsEnabled
  });

  // Emergency contact fields — used by report_emergency for forwarding +
  // SMS/email alerts when the bot fires an emergency. As of 2026-06-05
  // these target columns on the emergency-slug RoutingGroupEntity row
  // instead of LocationVars (the four legacy `CortexEmergency*` keys
  // were deleted). Apply/preview finds-or-creates the routing-group row
  // at the same scope as the source LocationVar.
  specs.push({
    id: "emergency.forward-number",
    groupLabel: GROUP_EMERGENCY_ROUTING,
    label: "Emergency forward number (primary)",
    fromKey: LocationVarKey.VoiceAfterHoursEmergencyForwardPhoneNumber,
    routingGroup: { slug: "emergency", column: "escalationForwardPhoneNumber" }
  });
  specs.push({
    id: "emergency.after-hours-phone",
    groupLabel: GROUP_EMERGENCY_ROUTING,
    label: "Emergency forward number (after-hours)",
    fromKey: LocationVarKey.AfterHoursEmergencyPhone,
    routingGroup: { slug: "emergency", column: "outsideHoursForwardPhoneNumber" }
  });
  specs.push({
    id: "emergency.alert-sms-recipients",
    groupLabel: GROUP_EMERGENCY_ROUTING,
    label: "Emergency SMS-alert recipients",
    fromKey: LocationVarKey.VoiceAfterHoursEmergencySMSPhoneNumbers,
    routingGroup: { slug: "emergency", column: "alertSmsRecipients" },
    transform: parseCsvRecipients
  });
  specs.push({
    id: "emergency.alert-email-recipients",
    groupLabel: GROUP_EMERGENCY_ROUTING,
    label: "Emergency email-alert recipients",
    fromKey: LocationVarKey.VoiceAfterHoursEmergencyEmails,
    routingGroup: { slug: "emergency", column: "alertEmailRecipients" },
    transform: parseCsvRecipients
  });

  // Copy the app's existing per-location `Timezone` var into the Cortex var the hours engine reads, validated as IANA so a bad zone can't skew every open/closed answer.
  specs.push({
    id: "facility-info.timezone",
    groupLabel: GROUP_HOURS,
    label: "Timezone",
    fromKey: LocationVarKey.Timezone,
    toKey: LocationVarKey.CortexFacilityTimezone,
    isTimezone: true
  });

  // Migrate the legacy freeform hours strings into structured Schedule rows; the service parses the text and quarantines anything unparseable.
  specs.push({
    id: "hours.office",
    groupLabel: GROUP_HOURS,
    label: "Office Hours",
    fromKey: LocationVarKey.OfficeHours,
    schedule: { slug: "office-hours", label: "Office Hours" }
  });
  specs.push({
    id: "hours.gate-access",
    groupLabel: GROUP_HOURS,
    label: "Gate Access Hours",
    fromKey: LocationVarKey.AccessHours,
    schedule: { slug: "gate-access", label: "Gate Access" }
  });
  specs.push({
    id: "hours.call-center",
    groupLabel: GROUP_HOURS,
    label: "Call Center Hours",
    fromKey: LocationVarKey.CallCenterHours,
    schedule: { slug: "call-center", label: "Call Center Hours" }
  });

  // ENG-2893: the 2-color brand model. Entity-targeted — the service reads
  // the org's Brand rows itself (no LocationVar source, hence no fromKey) and
  // writes each brand's `colors` from theme.primaryBackground/secondaryBackground.
  specs.push({
    id: "brands.colors",
    groupLabel: GROUP_BRANDS,
    label: "Brand colors (primary + secondary)",
    brandColors: true
  });

  return specs;
}
